'use client'

import ChevronLeft from '@/components/icons/chevron-left'
import ChevronRight from '@/components/icons/chevron-right'
import { useEffect, useRef, useState } from 'react'

/** Usage example:

<ButtonCarousel className="p-5">
  {Array(10)
    .fill()
    .map((i, _i) => (
      <div key={`carousel-item-${_i}`} className="shrink-0">
        <div className="w-96 rounded-lg bg-white text-neutral-blue-900 shadow-xl">
          <div className="flex flex-col gap-3">
            <h2 className="text-xl font-semibold leading-tight">Card!</h2>
            <p>Cards are amazing!</p>
            <div className="mt-2 flex items-center justify-end gap-2">
              <button className="inline-flex items-center justify-center gap-2 rounded-sm border border-primary bg-primary px-4 py-2 text-center text-sm font-medium text-shades-0 transition-colors duration-200 hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60">
                Button
              </button>
            </div>
          </div>
        </div>
      </div>
    ))}
</ButtonCarousel>
 */

export default function ButtonCarousel({
  children,
  showMobileDots = false,
  className = '',
}) {
  const tracker = useRef()

  const [position, setPosition] = useState(0)
  const [hideBackwardBtn, setHideForwardBtn] = useState(true)
  const [hideForwardBtn, setHideBackwardBtn] = useState(true)

  useEffect(() => {
    if (tracker) {
      setHideBackwardBtn(
        tracker.current.scrollWidth <= tracker.current.offsetWidth,
      )
    }
  }, [tracker])

  function handleScroll() {
    const position = tracker.current.scrollLeft
    setPosition(position)
    setHideForwardBtn(position <= 0)
    setHideBackwardBtn(
      position + tracker.current.clientWidth >= tracker.current.scrollWidth,
    )
  }

  function scroll(direction) {
    const itemWidth = (tracker.current.children[0]?.offsetWidth || 0) + 16
    const position = tracker.current.scrollLeft
    if (direction === 'forward') {
      tracker.current.scroll(position - itemWidth, 0)
    } else {
      tracker.current.scroll(position + itemWidth, 0)
    }
  }

  return (
    <div className={`relative ${className}`}>
      <div
        onClick={() => scroll('forward')}
        className={`bg-linear-to-r absolute bottom-0 left-0 top-0 z-10 hidden w-[10%] cursor-pointer items-center justify-center from-neutral-blue-900 to-transparent md:flex ${
          hideBackwardBtn ? 'hidden!' : 'animate__animated animate__fadeIn'
        }`}
      >
        <button className="relative inline-flex h-10 h-7 min-h-7 w-10 shrink-0 grow-0 items-center justify-center gap-2 rounded-full rounded-sm border border-neutral-blue-200 border-transparent bg-neutral-blue-200 p-0 px-2 px-4 py-2 text-center text-sm text-xs font-medium normal-case text-neutral-blue-900 transition-colors duration-200 hover:bg-neutral-blue-200/90 disabled:cursor-not-allowed disabled:opacity-60 sm:h-9 sm:min-h-9 sm:px-3 sm:text-sm">
          <ChevronLeft className="h-4 w-4 stroke-black stroke-[2px]" />
        </button>
      </div>

      <div
        className="no-scrollbar flex snap-x snap-proximity flex-row items-center gap-4 overflow-x-auto scroll-smooth motion-reduce:scroll-auto [&>*]:shrink-0 [&>*]:snap-start"
        ref={tracker}
        onScroll={handleScroll}
      >
        {children}
      </div>

      <div
        onClick={() => scroll('backward')}
        className={`bg-linear-to-l absolute bottom-0 right-0 top-0 z-10 hidden w-[10%] cursor-pointer items-center justify-center from-neutral-blue-900 to-transparent md:flex ${
          hideForwardBtn ? 'hidden!' : 'animate__animated animate__fadeIn'
        }`}
      >
        <button className="relative inline-flex h-10 h-7 min-h-7 w-10 shrink-0 grow-0 items-center justify-center gap-2 rounded-full rounded-sm border border-neutral-blue-200 border-transparent bg-neutral-blue-200 p-0 px-2 px-4 py-2 text-center text-sm text-xs font-medium normal-case text-neutral-blue-900 transition-colors duration-200 hover:bg-neutral-blue-200/90 disabled:cursor-not-allowed disabled:opacity-60 sm:h-9 sm:min-h-9 sm:px-3 sm:text-sm">
          <ChevronRight className="h-4 w-4 stroke-black stroke-[2px]" />
        </button>
      </div>
      {showMobileDots && (
        <div className="mt-5 flex w-full items-center justify-center gap-2 desktop-xs:hidden">
          {Array(children.length)
            .fill()
            .map((item, index) => (
              <span
                className={`h-[6px] w-[6px] rounded-full ${
                  index ===
                  Math.floor(
                    position / tracker.current?.children[index].offsetWidth,
                  )
                    ? 'bg-shades-0'
                    : 'bg-neutral-blue-500'
                }`}
                key={`dot-${index}`}
              ></span>
            ))}
        </div>
      )}
    </div>
  )
}
